'use client';

import {
	ArrowsRightLeftIcon,
	ChartBarIcon,
	GlobeAltIcon,
	LinkIcon,
	QrCodeIcon,
	RectangleStackIcon,
	ShieldCheckIcon,
	StarIcon,
	TagIcon,
} from '@heroicons/react/24/outline';
import { Link } from '@/i18n/navigation';
import { Badge } from '@/components/ui/badge';
import Container from '@/components/ui/container';
import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useAuth } from '@/auth/client';

interface FeatureCardData {
	icon: ReactNode;
	headlineKey: string;
	subHeadlineKey: string;
	badge?: string;
	actionLabel?: string;
	actionHref?: string;
	authOnly?: boolean;
}

export function FeatureSlider() {
	const t = useTranslations('contentElements.featuresCta');
	const tGeneral = useTranslations('general');
	const { isSignedIn } = useAuth();

	const features: FeatureCardData[] = [
		{
			icon: <LinkIcon className="h-6 w-6" />,
			headlineKey: 'shortUrlFeature.headline',
			subHeadlineKey: 'shortUrlFeature.subHeadline',
			actionLabel: t('shortUrlFeature.actionLabel'),
			actionHref: '/dashboard/short-urls',
			authOnly: true,
		},
		{
			icon: <ShieldCheckIcon className="h-6 w-6" />,
			headlineKey: 'secureFeature.headline',
			subHeadlineKey: 'secureFeature.subHeadline',
		},
		{
			icon: <QrCodeIcon className="h-6 w-6" />,
			headlineKey: 'editFeature.headline',
			subHeadlineKey: 'editFeature.subHeadline',
		},
		{
			icon: <ChartBarIcon className="h-6 w-6" />,
			headlineKey: 'statisticFeature.headline',
			subHeadlineKey: 'statisticFeature.subHeadline',
		},
		{
			icon: <GlobeAltIcon className="h-6 w-6" />,
			headlineKey: 'customDomainFeature.headline',
			subHeadlineKey: 'customDomainFeature.subHeadline',
			badge: tGeneral('comingSoon'),
		},
		{
			icon: <RectangleStackIcon className="h-6 w-6" />,
			headlineKey: 'overviewFeature.headline',
			subHeadlineKey: 'overviewFeature.subHeadline',
		},
		{
			icon: <StarIcon className="h-6 w-6" />,
			headlineKey: 'templateFeature.headline',
			subHeadlineKey: 'templateFeature.subHeadline',
		},
		{
			icon: <TagIcon className="h-6 w-6" />,
			headlineKey: 'tagFeature.headline',
			subHeadlineKey: 'tagFeature.subHeadline',
		},
		{
			icon: <ArrowsRightLeftIcon className="h-6 w-6" />,
			headlineKey: 'exportImportFeature.headline',
			subHeadlineKey: 'exportImportFeature.subHeadline',
		},
	];

	return (
		<Container disableOverflow>
			{/* Section Header */}
			<div className="mb-8 sm:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-5 sm:gap-6">
				<div className="max-w-2xl">
					<div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-semibold uppercase tracking-wider mb-2.5 sm:mb-3">
						Platform Capabilities
					</div>
					<h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
						{t('headline')}
					</h2>
					<p className="mt-2.5 sm:mt-3 text-sm sm:text-lg text-slate-600 leading-relaxed">
						{t('subHeadline')}
					</p>
				</div>
				<Link
					href="/features"
					className="inline-flex items-center gap-1.5 text-sm sm:text-base font-semibold text-emerald-800 hover:text-emerald-950 hover:underline transition-colors whitespace-nowrap self-start md:self-end"
				>
					{t('allFeatures')}
					<span aria-hidden="true">&rsaquo;</span>
				</Link>
			</div>

			{/* Full Features Grid - All 9 cards visible without scrolling */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
				{features.map((feature, index) => (
					<motion.div
						key={feature.headlineKey}
						className="group relative flex flex-col justify-between bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-slate-200/80 hover:border-emerald-500/40 shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.1 }}
						transition={{
							duration: 0.4,
							delay: index * 0.05,
							ease: 'easeOut',
						}}
					>
						<div>
							{/* Card Header: Icon & optional Badge */}
							<div className="flex items-center justify-between gap-3 mb-5">
								<div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center group-hover:bg-[#0f766e] group-hover:text-white transition-colors duration-300">
									{feature.icon}
								</div>
								{feature.badge && (
									<Badge variant="blue" className="text-xs font-medium">
										{feature.badge}
									</Badge>
								)}
							</div>

							{/* Title & SubHeadline */}
							<h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 leading-snug group-hover:text-[#0f766e] transition-colors">
								{t(feature.headlineKey)}
							</h3>
							<p className="text-slate-600 text-sm sm:text-[15px] leading-relaxed">
								{t(feature.subHeadlineKey)}
							</p>
						</div>

						{/* Action Link if provided */}
						{feature.actionHref && feature.actionLabel && (!feature.authOnly || isSignedIn) && (
							<div className="pt-5 mt-5 border-t border-slate-100">
								<Link
									href={feature.actionHref}
									className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-900 transition-colors"
								>
									{feature.actionLabel}
									<span aria-hidden="true">&rsaquo;</span>
								</Link>
							</div>
						)}
					</motion.div>
				))}
			</div>
		</Container>
	);
}
